/**
 * Who was assigned to which worklist, for which facilities, on which days (reviews
 * #405, #406): the Worklist assignments page and a claim's attribution panel.
 *
 * A separate artefact (api/worklist-assignments; scripts/build_bundle.py
 * worklist_assignments), per person in open.json's shape, fetched only when one of
 * those opens. Each row is an unbroken run of days on which one person was assigned to
 * one worklist that covered one facility, read off Medevolve's configuration history
 * (lhLaborModel scorecard_v2.worklist_snapshot_scd, the table behind the VP Labor
 * Tool's "Worklist Data (Raw)" page). Only the people the caller may see are in it.
 */
import { useCachedDetail } from './detailFetch'

export interface PersonAssignments {
  n: number
  dict: {
    /** Worklist names. */
    w: string[]
    /** Facility names. */
    f: string[]
    /** Days, 'YYYY-MM-DD', for `a` and `b`. */
    d: string[]
  }
  cols: {
    /** The worklist's Medevolve id (WFC_Key): what a link carries. */
    wk: number[]
    w: number[]
    f: number[]
    /** First and last day of the run, inclusive, into `dict.d`. */
    a: number[]
    b: number[]
    /** 1 for a system list. */
    sys: (0 | 1)[]
    /** The worklist's priority in today's configuration; null when it has none. */
    pr: (number | null)[]
    /**
     * The facility's Medevolve id (RC key), what a ClaimID starts with. Claims and
     * assignments are matched on it, not on the name: the two name the same facility
     * differently two times in three. Absent from a build before 2026-10-12.
     */
    fid?: number[]
  }
  /**
   * Each of the person's worklists' priority over time, by id (DimWorklistHistory):
   * [from, to, priority] spans, days inclusive. Absent from a build without it.
   */
  prh?: Record<string, PrioritySpan[]>
}

/** [from, to, priority]: days inclusive, 'YYYY-MM-DD'. */
export type PrioritySpan = [string, string, number]

export interface AssignmentsData {
  version: string
  generatedAt: string
  n: number
  /** First and last day of the history; it starts 2026-03-01. */
  from: string | null
  to: string | null
  /** The first day the priority history covers (2026-07-07); absent without it. */
  priorityFrom?: string | null
  people: Record<string, PersonAssignments>
}

/** One run, decoded. */
export interface AssignmentRow {
  person: string
  worklistId: number
  worklist: string
  facility: string
  /** Medevolve's facility id; null from a build without it. */
  facilityId: number | null
  from: string
  to: string
  system: boolean
  /** Today's priority. */
  priority: number | null
  /** The worklist's priority over time; empty when the build carries none. */
  priorities: readonly PrioritySpan[]
}

const URL_ = (import.meta.env.VITE_ASSIGNMENTS_URL as string | undefined) ?? '/api/worklist-assignments'

export function useAssignments(
  enabled = true,
): { data: AssignmentsData | null; error: string | null } {
  return useCachedDetail<AssignmentsData>(
    'worklist-assignments',
    URL_,
    'Worklist assignments arrive with the next data build.',
    enabled,
  )
}

/** Every run, one object each. A few thousand at most: small enough to keep as objects. */
export function decodeAssignments(data: AssignmentsData): AssignmentRow[] {
  const out: AssignmentRow[] = []
  const none: PrioritySpan[] = []
  for (const [person, p] of Object.entries(data.people)) {
    for (let i = 0; i < p.n; i++) {
      out.push({
        person,
        worklistId: p.cols.wk[i]!,
        worklist: p.dict.w[p.cols.w[i]!] ?? '',
        facility: p.dict.f[p.cols.f[i]!] ?? '',
        facilityId: p.cols.fid?.[i] ?? null,
        from: p.dict.d[p.cols.a[i]!] ?? '',
        to: p.dict.d[p.cols.b[i]!] ?? '',
        system: p.cols.sys[i] === 1,
        priority: p.cols.pr[i] ?? null,
        priorities: p.prh?.[String(p.cols.wk[i])] ?? none,
      })
    }
  }
  return out
}

/** The priority on `day`, from the history; null for a day it does not cover. */
export function priorityOn(spans: readonly PrioritySpan[], day: string): number | null {
  for (const [a, b, pr] of spans) if (a <= day && day <= b) return pr
  return null
}

/**
 * The priorities a worklist had over `from`–`to`, as the page shows them: one number,
 * or the lowest and highest. `covered` is false when the history misses some of those
 * days (it starts 2026-07-07), so the page can say the figure is partial or absent.
 */
export function prioritiesIn(
  spans: readonly PrioritySpan[],
  from: string,
  to: string,
): { min: number; max: number; covered: boolean } | null {
  let min = Infinity
  let max = -Infinity
  let firstCovered: string | null = null
  for (const [a, b, pr] of spans) {
    if (b < from || a > to) continue
    min = Math.min(min, pr)
    max = Math.max(max, pr)
    if (firstCovered === null || a < firstCovered) firstCovered = a
  }
  if (min === Infinity) return null
  return { min, max, covered: firstCovered !== null && firstCovered <= from }
}

/** "783", or "783–795" for a range. */
export const priorityText = (p: { min: number; max: number }) =>
  p.min === p.max ? String(p.min) : `${p.min}–${p.max}`

/** 'YYYY-MM' -> its first and last day. */
export function monthBounds(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` }
}

/** Whether a run touches the days `from`–`to` (inclusive; ISO dates compare as text). */
export const overlapsDays = (r: { from: string; to: string }, from: string, to: string) =>
  r.from <= to && r.to >= from

/** Days in a run, inclusive. */
export function daysIn(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
}

/**
 * For each run, the other runs covering the same facility on some of the same days:
 * another person on the same worklist (`shared`: the model credits a claim on such a
 * list to the first of them by email address), or another worklist altogether
 * (`competing`). The second is review #406's shape, two billers' lists both covering
 * one facility from 7/1, but it is a candidate, not a finding: whether two lists match
 * the same claims depends on their payer type, service type and plan setup, which the
 * app does not have. Where both match, the matcher takes the higher priority.
 */
export function overlapsOf(
  rows: readonly AssignmentRow[],
): Map<AssignmentRow, { shared: AssignmentRow[]; competing: AssignmentRow[] }> {
  const byFacility = new Map<string, AssignmentRow[]>()
  for (const r of rows) {
    const k = r.facilityId === null ? `n:${r.facility}` : `id:${r.facilityId}`
    let l = byFacility.get(k)
    if (!l) byFacility.set(k, (l = []))
    l.push(r)
  }
  const out = new Map<AssignmentRow, { shared: AssignmentRow[]; competing: AssignmentRow[] }>()
  for (const list of byFacility.values()) {
    const sorted = [...list].sort((a, b) => a.from.localeCompare(b.from))
    for (let i = 0; i < sorted.length; i++) {
      const r = sorted[i]!
      for (let j = i + 1; j < sorted.length; j++) {
        const o = sorted[j]!
        if (o.from > r.to) break
        if (o.person === r.person && o.worklistId === r.worklistId) continue
        // Two system lists, or a system list beside a real one, are not a contest for
        // a claim: the matcher never predicts to a system list.
        if (r.system || o.system) continue
        const kind = o.worklistId === r.worklistId ? 'shared' : 'competing'
        for (const [x, y] of [[r, o], [o, r]] as const) {
          let e = out.get(x)
          if (!e) out.set(x, (e = { shared: [], competing: [] }))
          e[kind].push(y)
        }
      }
    }
  }
  return out
}

/** Ids → the worklist's name, as the runs carry it (the latest run's name wins). */
export function worklistNames(rows: readonly AssignmentRow[]): Map<number, string> {
  const out = new Map<number, { name: string; to: string }>()
  for (const r of rows) {
    const cur = out.get(r.worklistId)
    if (!cur || r.to > cur.to) out.set(r.worklistId, { name: r.worklist, to: r.to })
  }
  return new Map([...out].map(([k, v]) => [k, v.name]))
}

/** The months ('YYYY-MM') the history spans, oldest first. */
export function monthsBetween(from: string | null, to: string | null): string[] {
  if (!from || !to) return []
  const out: string[] = []
  let [y, m] = from.slice(0, 7).split('-').map(Number) as [number, number]
  const end = to.slice(0, 7)
  for (let guard = 0; guard < 240; guard++) {
    const ym = `${y}-${String(m).padStart(2, '0')}`
    out.push(ym)
    if (ym >= end) break
    m += 1
    if (m > 12) {
      m = 1
      y += 1
    }
  }
  return out
}

/** The Worklist assignments page at a worklist and a month: Claim detail's link. */
export function assignmentsLink(worklistId: number, month?: string | null): string {
  const q = new URLSearchParams({ worklist: String(worklistId) })
  if (month) q.set('month', month)
  return `/worklist-assignments?${q.toString()}`
}
